import type {
  ClassDef,
  Combat,
  ItemStats,
  JewelryTable,
  StatKey,
  UpgradeTable,
  UpgradeTables,
} from "./schema";

export type StatBlock = Record<StatKey, number>;

export interface DerivedStats {
  maxHp: number;
  maxMp: number;
  /** Attack power before buffs. */
  ap: number;
  /** Defense before buffs (gear only, 0 until items arrive). */
  def: number;
  /** 0..1 */
  critChance: number;
}

/**
 * PRD base-stat formulas. Shared by the server (authoritative) and the character screen
 * (preview), so both always show the same numbers.
 * - HP = hpBase + hpPerVit * VIT, plus the class bonus
 * - MP = mpBase + mpPerInt * INT
 * - AP = weapon base + primary stat * apPerPrimary
 * - Crit = DEX * critPerDex, capped
 */
/** How far along the level curve a level is: 0 up to `from`, 1 at the cap (combat.levelGrowth). */
export function levelShare(level: number, cfg: Combat): number {
  const g = cfg.levelGrowth;
  const x = Math.min(1, Math.max(0, (level - g.from) / Math.max(1, g.cap - g.from)));
  return x ** g.power;
}

/** A monster's HP and attack power grow with its level like a character's do. */
export function mobLevelMult(level: number, cfg: Combat): number {
  return 1 + levelShare(level, cfg) * cfg.levelGrowth.mobBonus;
}

export function deriveStats(
  cls: ClassDef,
  base: StatBlock,
  cfg: Combat,
  gear: GearBonus = NO_GEAR,
  level = 1,
): DerivedStats {
  if (cfg.ko && cls.ko) return koDerive(cls.ko, base, gear, level, cfg, cls.id === "rogue");
  const share = levelShare(level, cfg);
  const s: StatBlock = {
    str: base.str + (gear.str ?? 0),
    dex: base.dex + (gear.dex ?? 0),
    int: base.int + (gear.int ?? 0),
    vit: base.vit + (gear.vit ?? 0),
  };
  // An equipped weapon replaces the class's training weapon.
  const weaponAp = gear.weaponAp ?? cls.weapon.apBase;
  return {
    maxHp:
      Math.round((cfg.hpBase + cfg.hpPerVit * s.vit) * (1 + cls.hpBonusPct / 100)) +
      (gear.hp ?? 0) +
      Math.round(share * cls.levelHp),
    maxMp:
      Math.round(cfg.mpBase + cfg.mpPerInt * s.int) +
      (gear.mp ?? 0) +
      Math.round(share * cls.levelMp),
    ap:
      weaponAp +
      (gear.ap ?? 0) +
      s[cls.primary] * cfg.apPerPrimary +
      Math.round(share * cls.levelAp),
    def: gear.def ?? 0,
    critChance: Math.min(cfg.critCap, s.dex * cfg.critPerDex),
  };
}

/**
 * Local experiment: ko's CUser::SetUserAbility, SetMaxHp and SetMaxMp. `vit` is ko's stamina.
 * Attack: 0.005 x weapon x (STAT + 40) + coefficient x weapon x level x STAT + 3, STAT being a
 * rogue's DEX and everyone else's STR, worn items' points included (owner 2026-10-29: a DEX
 * piece made no rogue stronger); defense: the
 * class's AC x (level + armor); HP: HP x level^2 x STA + 0.1 x level x STA + STA / 5; MP from
 * INT + 30 for the casters, SP from STA for the fighters (shown as MP).
 */
function koDerive(
  k: NonNullable<ClassDef["ko"]>,
  base: StatBlock,
  gear: GearBonus,
  level: number,
  cfg: Combat,
  byDex = false,
): DerivedStats {
  const str = base.str + (gear.str ?? 0);
  const sta = base.vit + (gear.vit ?? 0);
  const intel = base.int + (gear.int ?? 0) + 30;
  const dex = base.dex + (gear.dex ?? 0);
  // m_sItemHit: every worn item's damage added up (gearBonus keeps the weapon's apart)
  const weapon = (gear.weaponAp ?? 0) + (gear.ap ?? 0);
  const L = level;
  const mp = k.mp
    ? k.mp * L * L * intel + 0.1 * L * 2 * intel + intel / 5 + 20
    : k.sp * L * L * sta + 0.1 * L * sta + sta / 5;
  return {
    maxHp: Math.max(5, Math.floor(k.hp * L * L * sta + 0.1 * L * sta + sta / 5) + (gear.hp ?? 0)),
    maxMp: Math.max(0, Math.floor(mp) + (gear.mp ?? 0)),
    ap: Math.floor(
      0.005 * weapon * ((byDex ? dex : str) + 40) + k.hit * weapon * L * (byDex ? dex : str) + 3,
    ),
    def: Math.floor(k.ac * (L + (gear.def ?? 0))),
    critChance: Math.min(cfg.critCap, dex * cfg.critPerDex),
  };
}

/** Summed bonuses of everything equipped. `weaponAp` is the weapon's own attack power. */
export type GearBonus = ItemStats & { weaponAp?: number };
const NO_GEAR: GearBonus = {};

export interface EquippedItem {
  /** `plusBonus`: KO's own + ladder (its Item_Ext rows): what each + level adds, in place of the
   * upgrade table's growth (local experiment). */
  template: {
    slot: string;
    stats: ItemStats;
    plusBonus?: Record<string, ItemStats>;
    plusBonusMagic?: Record<string, ItemStats>;
    plusBonusRare?: Record<string, ItemStats>;
  };
  /** The copy's grade: a magic or rare KO item climbs its own kind's ladder. */
  grade?: string;
  rolls: { stat: keyof ItemStats; value: number }[];
  /** Upgrade level (+N). Raises the template's base stats by the upgrade table's gains. */
  plus?: number;
}

/** Slots that use the jewelry ladder; everything else equippable uses the gear ladder. */
export const JEWELRY_SLOTS: ReadonlySet<string> = new Set([
  "ring",
  "neck",
  "earring",
  "belt",
  "cape",
]);
/** Slots that cannot be upgraded at all (store cosmetics). */
export const NO_UPGRADE_SLOTS: ReadonlySet<string> = new Set([
  "glow",
  "wings",
  "mantle",
  "reward",
  "costume",
  "none",
]);

/** The upgrade ladder an item of this slot climbs, or undefined if it cannot be upgraded. */
export function upgradeTableFor(
  tables: UpgradeTables,
  slot: string,
): UpgradeTable | JewelryTable | undefined {
  if (NO_UPGRADE_SLOTS.has(slot)) return undefined;
  return JEWELRY_SLOTS.has(slot) ? tables.jewelry : tables.gear;
}

/** Cumulative base-stat multiplier for a +N item (PRD +upgrade table: +5%, +5%, +6%, ...). */
export function plusMultiplier(
  plus: number,
  table: { steps: { from: number; statGainPct: number }[] },
): number {
  let pct = 0;
  for (const step of table.steps) if (step.from < plus) pct += step.statGainPct;
  return 1 + pct / 100;
}

/** The + ladder a KO item of this grade climbs (plain, magic or rare kind). */
export function koLadder(
  t: {
    plusBonus?: Record<string, ItemStats>;
    plusBonusMagic?: Record<string, ItemStats>;
    plusBonusRare?: Record<string, ItemStats>;
  },
  grade?: string,
): Record<string, ItemStats> | undefined {
  if (grade === "rare" || grade === "unique")
    return t.plusBonusRare ?? t.plusBonusMagic ?? t.plusBonus;
  if (grade === "magic") return t.plusBonusMagic ?? t.plusBonus;
  return t.plusBonus;
}

/** KO's percent lines (drain, hit, evasion): flat like the resistances, a + level keeps them. */
const FLAT_PCT: ReadonlySet<string> = new Set(["hpDrain", "mpDrain", "hit", "evade"]);

export function gearBonus(items: EquippedItem[], tables?: UpgradeTables): GearBonus {
  const out: GearBonus = {};
  for (const it of items) {
    const table = tables && upgradeTableFor(tables, it.template.slot);
    const ko = koLadder(it.template, it.grade);
    const mult = ko ? 1 : it.plus && table ? plusMultiplier(it.plus, table) : 1;
    // an enchanted copy's ladder has a "0": its stat from +0 on
    if (ko)
      for (const [k, v] of Object.entries(ko[String(Math.min(10, it.plus ?? 0))] ?? {}) as [
        keyof ItemStats,
        number,
      ][]) {
        if (k === "ap" && it.template.slot === "weapon") out.weaponAp = (out.weaponAp ?? 0) + v;
        else out[k] = (out[k] ?? 0) + v;
      }
    for (const [k, raw] of Object.entries(it.template.stats) as [keyof ItemStats, number][]) {
      // Class guards and elemental resistances are flat percentages: a + level does not grow them.
      const v = k.startsWith("res") || FLAT_PCT.has(k) ? raw : Math.round(raw * mult);
      if (k === "ap" && it.template.slot === "weapon") out.weaponAp = (out.weaponAp ?? 0) + v;
      else out[k] = (out[k] ?? 0) + v;
    }
    for (const r of it.rolls) out[r.stat] = (out[r.stat] ?? 0) + r.value;
  }
  return out;
}
